import React, { useEffect, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CHALLENGE_MISSION_MAX,
  CHALLENGE_SUBMISSION_MODE_LABEL,
  CHALLENGE_TEXT_MAX,
  missionNeedsPhoto,
  missionNeedsText,
  type ChallengeContent,
  type ChallengeCopy,
  type ChallengeMission,
  type ChallengeProjectIntro,
  type ChallengeSubmissionMode,
} from '../../types/assignmentChallenge';

export type ChallengeStageItem = {
  title: string;
  content: ChallengeContent;
  thumbnailUrl: string | null;
};

export type ChallengeEditApi = {
  setTitle: (v: string) => void;
  setCopy: (key: keyof ChallengeCopy, v: string) => void;
  updateMission: (id: string, patch: Partial<ChallengeMission>) => void;
  addMission: () => void;
  removeMission: (id: string) => void;
  reorderMissions: (activeId: string, overId: string) => void;
  openThumbnail: () => void;
};

export type ChallengeLiveAnswer = {
  text: string;
  photoUrl: string | null;
  uploading: boolean;
  error?: string;
};

export type ChallengeLiveFormApi = {
  answers: Record<string, ChallengeLiveAnswer>;
  onText: (missionId: string, v: string) => void;
  onPhoto: (missionId: string, file: File) => void;
  onRemovePhoto: (missionId: string) => void;
  readOnly: boolean;
  submitting: boolean;
  canSubmit: boolean;
  submitLabel?: string;
  onSubmit: () => void;
  participant?: React.ReactNode;
  notice?: React.ReactNode;
};

// ───────────────────────── 인라인 편집 텍스트 ─────────────────────────

type InlineTextProps = {
  value: string;
  editable: boolean;
  onChange?: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
  label: string;
  className?: string;
  as?: 'span' | 'h1' | 'h2' | 'h3' | 'p' | 'div';
};

export const ChallengeInlineText: React.FC<InlineTextProps> = ({
  value,
  editable,
  onChange,
  placeholder = '',
  multiline = false,
  maxLength = 500,
  label,
  className = '',
  as = 'span',
}) => {
  const [editing, setEditing] = useState(false);
  const editorRef = useRef<HTMLSpanElement | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const cancelRef = useRef(false);

  useEffect(() => {
    if (!editing) return;
    const el = editorRef.current;
    if (!el) return;
    cancelRef.current = false;
    el.textContent = valueRef.current;
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }, [editing]);

  const Tag = as;
  if (!editable) {
    if (!value) return null;
    return <Tag className={className}>{value}</Tag>;
  }

  const commit = () => {
    const el = editorRef.current;
    setEditing(false);
    if (cancelRef.current || !el) return;
    let next = (el.innerText ?? '').replace(/\u00a0/g, ' ');
    next = multiline ? next.replace(/\n+$/, '') : next.replace(/\s*\n\s*/g, ' ').trim();
    next = next.slice(0, maxLength);
    if (next !== value) onChange?.(next);
  };

  if (editing) {
    return (
      <Tag className={`${className} cp-chl-inline is-editing`}>
        <span
          ref={editorRef}
          className={`cp-chl-inline-text cp-chl-inline-editor${multiline ? ' is-multiline' : ''}`}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label={label}
          aria-multiline={multiline || undefined}
          data-placeholder={placeholder}
          onBlur={commit}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, multiline ? text : text.replace(/\s*\n\s*/g, ' '));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              cancelRef.current = true;
              e.currentTarget.blur();
            } else if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
        />
      </Tag>
    );
  }

  return (
    <Tag
      className={`${className} cp-chl-inline is-editable${value ? '' : ' is-empty'}`}
      onDoubleClick={() => setEditing(true)}
      title="더블클릭해서 수정"
    >
      <span className="cp-chl-inline-text">{value || placeholder}</span>
      <button
        type="button"
        className="cp-chl-inline-pencil"
        aria-label={`${label} 수정`}
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </Tag>
  );
};

// ───────────────────────── 공통 조각 ─────────────────────────

const CameraIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg className="cp-chl-flat-icon" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <path
      d="M9.2 4.5c-.5 0-1 .26-1.27.69L7.1 6.5H5A2.5 2.5 0 0 0 2.5 9v8.5A2.5 2.5 0 0 0 5 20h14a2.5 2.5 0 0 0 2.5-2.5V9A2.5 2.5 0 0 0 19 6.5h-2.1l-.83-1.31a1.5 1.5 0 0 0-1.27-.69H9.2z"
      fill="currentColor"
    />
    <circle cx="12" cy="13.2" r="4" fill="#fff" />
    <circle cx="12" cy="13.2" r="2.3" fill="currentColor" />
    <circle cx="18" cy="9.4" r="1" fill="#fff" />
  </svg>
);

const AlbumIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg className="cp-chl-flat-icon" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <rect x="2.5" y="4" width="19" height="16" rx="3" fill="currentColor" />
    <circle cx="8.6" cy="9.4" r="2" fill="#fff" />
    <path d="M4.5 18 9.8 12.3l3.4 3.4 2.4-2.4 3.9 4.7H4.5z" fill="#fff" />
  </svg>
);

export const ChallengeThumb: React.FC<{
  url: string | null;
  title: string;
  edit?: ChallengeEditApi | null;
  className?: string;
}> = ({ url, title, edit, className = '' }) => {
  const inner = url ? (
    <img src={url} alt="" className="cp-chl-thumb-img" />
  ) : (
    <span className="cp-chl-thumb-empty" aria-hidden>
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l2.9 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 7.1-1.01L12 2z" fill="currentColor" />
      </svg>
      <span>{title ? title.slice(0, 12) : '챌린지'}</span>
    </span>
  );
  if (!edit) return <div className={`cp-chl-thumb ${className}`}>{inner}</div>;
  return (
    <button
      type="button"
      className={`cp-chl-thumb is-editable ${className}`}
      onClick={edit.openThumbnail}
      aria-label="썸네일 변경"
    >
      {inner}
      <span className="cp-chl-thumb-edit-badge">썸네일 변경</span>
    </button>
  );
};

export const ChallengeLogoBar: React.FC<{
  text: string;
  position: 'top' | 'bottom';
  edit?: ChallengeEditApi | null;
}> = ({ text, position, edit }) => {
  if (!edit && !text) return null;
  const key = position === 'top' ? 'topLogoText' : 'bottomLogoText';
  return (
    <div className={`cp-chl-logo-bar is-${position}`}>
      <ChallengeInlineText
        value={text}
        editable={!!edit}
        onChange={(v) => edit?.setCopy(key, v)}
        placeholder={position === 'top' ? '상단 로고 문구 (비우면 숨김)' : '하단 로고 문구 (비우면 숨김)'}
        label={position === 'top' ? '상단 로고 문구' : '하단 로고 문구'}
        maxLength={60}
      />
    </div>
  );
};

// ───────────────────────── 인증 항목 ─────────────────────────

const MODE_OPTIONS: ChallengeSubmissionMode[] = ['photo_and_text', 'photo_only', 'text_only'];

function MissionEditorCard({
  mission,
  index,
  edit,
  canRemove,
}: {
  mission: ChallengeMission;
  index: number;
  edit: ChallengeEditApi;
  canRemove: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: mission.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 5 : undefined,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`cp-chl-mission is-edit${isDragging ? ' is-dragging' : ''}`}
    >
      <div className="cp-chl-mission-head">
        <button
          type="button"
          className="cp-chl-mission-drag"
          aria-label={`인증 항목 ${index + 1} 순서 이동 (스페이스로 집고 방향키로 이동)`}
          {...attributes}
          {...listeners}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </button>
        <span className="cp-chl-mission-no">{index + 1}</span>
        <ChallengeInlineText
          as="h3"
          className="cp-chl-mission-title"
          value={mission.title}
          editable
          onChange={(v) => edit.updateMission(mission.id, { title: v })}
          placeholder="인증 항목 제목"
          label="인증 항목 제목"
          maxLength={120}
        />
        <button
          type="button"
          className="cp-chl-mission-remove"
          disabled={!canRemove}
          aria-label={`인증 항목 ${index + 1} 삭제`}
          onClick={() => edit.removeMission(mission.id)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className="cp-chl-mode-row" role="radiogroup" aria-label="제출 방식">
        {MODE_OPTIONS.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mission.submissionMode === m}
            className={`cp-chl-mode-chip${mission.submissionMode === m ? ' is-active' : ''}`}
            onClick={() => edit.updateMission(mission.id, { submissionMode: m })}
          >
            {CHALLENGE_SUBMISSION_MODE_LABEL[m]}
          </button>
        ))}
      </div>
      <ChallengeInlineText
        as="p"
        className="cp-chl-mission-body"
        value={mission.body}
        editable
        onChange={(v) => edit.updateMission(mission.id, { body: v })}
        placeholder="인증 항목 설명"
        label="인증 항목 설명"
        multiline
        maxLength={1000}
      />
      {missionNeedsPhoto(mission.submissionMode) ? (
        <div className="cp-chl-photo-slot is-preview">
          <span className="cp-chl-photo-slot-icon" aria-hidden>
            <CameraIcon size={20} />
          </span>
          <ChallengeInlineText
            value={mission.photoHint}
            editable
            onChange={(v) => edit.updateMission(mission.id, { photoHint: v })}
            placeholder="사진 안내 (예: 완성한 작품이 잘 보이게 찍어 주세요)"
            label="사진 안내"
            maxLength={200}
          />
        </div>
      ) : null}
      {missionNeedsText(mission.submissionMode) ? (
        <div className="cp-chl-text-slot is-preview">
          <ChallengeInlineText
            className="cp-chl-text-label"
            value={mission.textLabel}
            editable
            onChange={(v) => edit.updateMission(mission.id, { textLabel: v })}
            placeholder="글 항목 이름 (예: 느낀 점)"
            label="글 항목 이름"
            maxLength={60}
          />
          <div className="cp-chl-textarea is-preview">
            <ChallengeInlineText
              value={mission.textPlaceholder}
              editable
              onChange={(v) => edit.updateMission(mission.id, { textPlaceholder: v })}
              placeholder="입력칸 안내 문구"
              label="입력칸 안내 문구"
              maxLength={200}
            />
          </div>
        </div>
      ) : null}
    </li>
  );
}

function MissionLiveCard({
  mission,
  index,
  live,
}: {
  mission: ChallengeMission;
  index: number;
  live: ChallengeLiveFormApi;
}) {
  const answer = live.answers[mission.id] ?? { text: '', photoUrl: null, uploading: false };
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const albumRef = useRef<HTMLInputElement | null>(null);
  const textId = `cp-chl-text-${mission.id}`;
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) live.onPhoto(mission.id, file);
  };
  return (
    <li className="cp-chl-mission">
      <div className="cp-chl-mission-head">
        <span className="cp-chl-mission-no">{index + 1}</span>
        <h3 className="cp-chl-mission-title">{mission.title || `인증 항목 ${index + 1}`}</h3>
        <span className="cp-chl-mission-mode">{CHALLENGE_SUBMISSION_MODE_LABEL[mission.submissionMode]}</span>
      </div>
      {mission.body ? <p className="cp-chl-mission-body">{mission.body}</p> : null}
      {missionNeedsPhoto(mission.submissionMode) ? (
        <div className="cp-chl-photo-slot">
          {answer.photoUrl ? (
            <div className="cp-chl-photo-preview">
              <img src={answer.photoUrl} alt={`${mission.title} 인증 사진`} />
              {answer.uploading ? <span className="cp-chl-photo-uploading">올리는 중…</span> : null}
              {!live.readOnly ? (
                <button
                  type="button"
                  className="cp-chl-photo-remove"
                  onClick={() => live.onRemovePhoto(mission.id)}
                  disabled={answer.uploading || live.submitting}
                  aria-label="사진 지우기"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" /></svg>
                </button>
              ) : null}
            </div>
          ) : (
            <p className="cp-chl-photo-hint">{mission.photoHint || '인증 사진을 올려 주세요.'}</p>
          )}
          {!live.readOnly ? (
            <div className="cp-chl-photo-btns">
              <button
                type="button"
                className="cp-chl-secondary-btn"
                onClick={() => cameraRef.current?.click()}
                disabled={answer.uploading || live.submitting}
              >
                <CameraIcon />
                사진 찍기
              </button>
              <button
                type="button"
                className="cp-chl-secondary-btn"
                onClick={() => albumRef.current?.click()}
                disabled={answer.uploading || live.submitting}
              >
                <AlbumIcon />
                앨범에서 고르기
              </button>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="cp-chl-hidden-file"
                onChange={pick}
                aria-label="카메라로 사진 찍기"
              />
              <input
                ref={albumRef}
                type="file"
                accept="image/*"
                className="cp-chl-hidden-file"
                onChange={pick}
                aria-label="앨범에서 사진 고르기"
              />
            </div>
          ) : null}
          {answer.error ? (
            <p className="cp-chl-field-error" role="alert">
              {answer.error}
            </p>
          ) : null}
        </div>
      ) : null}
      {missionNeedsText(mission.submissionMode) ? (
        <div className="cp-chl-text-slot">
          <label className="cp-chl-text-label" htmlFor={textId}>
            {mission.textLabel || '글로 인증하기'}
          </label>
          <textarea
            id={textId}
            className="cp-chl-textarea"
            value={answer.text}
            maxLength={CHALLENGE_TEXT_MAX}
            placeholder={mission.textPlaceholder || '내용을 입력해 주세요.'}
            readOnly={live.readOnly}
            disabled={live.submitting}
            rows={4}
            onChange={(e) => live.onText(mission.id, e.target.value)}
          />
          <span className="cp-chl-text-count">
            {answer.text.length}/{CHALLENGE_TEXT_MAX}
          </span>
        </div>
      ) : null}
    </li>
  );
}

export const ChallengeMissionScreen: React.FC<{
  item: ChallengeStageItem;
  edit?: ChallengeEditApi | null;
  live?: ChallengeLiveFormApi | null;
  onBack?: () => void;
}> = ({ item, edit, live, onBack }) => {
  const c = item.content;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const onDragEnd = (e: DragEndEvent) => {
    if (!edit || !e.over || e.active.id === e.over.id) return;
    edit.reorderMissions(String(e.active.id), String(e.over.id));
  };
  return (
    <section className="cp-chl-screen cp-chl-form">
      <div className="cp-chl-form-head">
        {onBack ? (
          <button type="button" className="cp-chl-back-btn" onClick={onBack} aria-label="챌린지 목록으로 돌아가기">
            ‹
          </button>
        ) : null}
        <div className="cp-chl-form-head-text">
          <ChallengeInlineText
            as="p"
            className="cp-chl-kicker"
            value={c.kicker}
            editable={!!edit}
            onChange={(v) => edit?.setCopy('kicker', v)}
            placeholder="제목 위 작은 문구"
            label="제목 위 작은 문구"
            maxLength={60}
          />
          {edit ? (
            <ChallengeInlineText
              as="h2"
              className="cp-chl-form-title"
              value={item.title}
              editable
              onChange={(v) => edit.setTitle(v)}
              placeholder="인증과제 제목"
              label="인증과제 제목"
              maxLength={200}
            />
          ) : (
            <h2 className="cp-chl-form-title">{item.title || '인증과제'}</h2>
          )}
        </div>
      </div>
      {live?.participant}
      <ChallengeInlineText
        as="p"
        className="cp-chl-desc"
        value={c.description}
        editable={!!edit}
        onChange={(v) => edit?.setCopy('description', v)}
        placeholder="인증과제 소개 (학생 목록 카드에도 보여요)"
        label="인증과제 소개"
        multiline
        maxLength={2000}
      />
      <ChallengeInlineText
        as="p"
        className="cp-chl-guide"
        value={c.guideText}
        editable={!!edit}
        onChange={(v) => edit?.setCopy('guideText', v)}
        placeholder="인증 안내 문구"
        label="인증 안내 문구"
        multiline
        maxLength={1000}
      />
      {edit ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={c.missions.map((m) => m.id)} strategy={verticalListSortingStrategy}>
            <ol className="cp-chl-missions">
              {c.missions.map((m, i) => (
                <MissionEditorCard
                  key={m.id}
                  mission={m}
                  index={i}
                  edit={edit}
                  canRemove={c.missions.length > 1}
                />
              ))}
            </ol>
          </SortableContext>
        </DndContext>
      ) : (
        <ol className="cp-chl-missions">
          {c.missions.map((m, i) =>
            live ? <MissionLiveCard key={m.id} mission={m} index={i} live={live} /> : null
          )}
        </ol>
      )}
      {edit ? (
        <button
          type="button"
          className="cp-chl-add-mission"
          onClick={edit.addMission}
          disabled={c.missions.length >= CHALLENGE_MISSION_MAX}
        >
          {c.missions.length >= CHALLENGE_MISSION_MAX
            ? `인증 항목은 최대 ${CHALLENGE_MISSION_MAX}개까지 만들 수 있어요`
            : `+ 인증 항목 추가 (${c.missions.length}/${CHALLENGE_MISSION_MAX})`}
        </button>
      ) : null}
      {live?.notice}
      <div className="cp-chl-actions">
        {edit ? (
          <span className="cp-chl-primary-btn is-preview">
            <ChallengeInlineText
              value={c.submitButton}
              editable
              onChange={(v) => edit.setCopy('submitButton', v)}
              placeholder="제출 버튼 문구"
              label="제출 버튼 문구"
              maxLength={30}
            />
          </span>
        ) : live && !live.readOnly ? (
          <button
            type="button"
            className="cp-chl-primary-btn"
            onClick={live.onSubmit}
            disabled={!live.canSubmit || live.submitting}
          >
            {live.submitting ? '제출하는 중…' : live.submitLabel || c.submitButton || '제출하기'}
          </button>
        ) : null}
      </div>
    </section>
  );
};

// ───────────────────────── 완료 팝업 ─────────────────────────

export const ChallengeSuccessCard: React.FC<{
  content: ChallengeContent;
  edit?: ChallengeEditApi | null;
  onClose?: () => void;
}> = ({ content, edit, onClose }) => (
  <div className="cp-chl-success-card">
    <div className="cp-chl-success-burst" aria-hidden>
      <svg width="34" height="34" viewBox="0 0 24 24">
        <path d="M5 12.5l4.2 4.2L19 7" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <ChallengeInlineText
      as="h3"
      className="cp-chl-success-title"
      value={content.successTitle}
      editable={!!edit}
      onChange={(v) => edit?.setCopy('successTitle', v)}
      placeholder="응원 문구"
      label="성공 팝업 응원 문구"
      maxLength={60}
    />
    <ChallengeInlineText
      as="p"
      className="cp-chl-success-msg"
      value={content.successMessage}
      editable={!!edit}
      onChange={(v) => edit?.setCopy('successMessage', v)}
      placeholder="제출 완료 안내"
      label="성공 팝업 안내 문구"
      multiline
      maxLength={300}
    />
    {onClose ? (
      <button type="button" className="cp-chl-primary-btn" onClick={onClose}>
        확인
      </button>
    ) : null}
  </div>
);

// ───────────────────────── 목록 화면 머리(프로젝트 설명) ─────────────────────────

export type ChallengeIntroEditApi = {
  setIntro: (key: 'eyebrow' | 'subtitle' | 'headline' | 'description', v: string) => void;
};

const FlagIcon: React.FC = () => (
  <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 21V4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path
      d="M5 4.5c2.6-1.4 5-1.4 7.2 0s4.6 1.4 6.8 0v9c-2.2 1.4-4.6 1.4-6.8 0s-4.6-1.4-7.2 0"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

export const ChallengeProjectHead: React.FC<{
  memberName: string;
  title: string;
  intro: ChallengeProjectIntro;
  done: number;
  total: number;
  onLeave?: () => void;
  /** 편집 미리보기에서만 넘긴다 */
  edit?: ChallengeIntroEditApi | null;
}> = ({ memberName, title, intro, done, total, onLeave, edit }) => (
  <>
    <div className="cp-chl-hero-top">
      <span className="cp-chl-hero-member">{memberName}님의 챌린지</span>
      {onLeave ? (
        <button type="button" className="cp-chl-ghost-btn" onClick={onLeave}>
          나가기
        </button>
      ) : null}
    </div>
    <div className="cp-chl-hero">
      <ChallengeInlineText
        as="p"
        className="cp-chl-hero-eyebrow"
        value={intro.eyebrow}
        editable={!!edit}
        onChange={(v) => edit?.setIntro('eyebrow', v)}
        placeholder="영문 문구 (예: FAMILY WALKING CHALLENGE)"
        label="맨 위 영문 문구"
        maxLength={60}
      />
      <ChallengeInlineText
        as="p"
        className="cp-chl-hero-subtitle"
        value={intro.subtitle}
        editable={!!edit}
        onChange={(v) => edit?.setIntro('subtitle', v)}
        placeholder="제목 위 한 줄 (예: 가족과 함께하는)"
        label="제목 위 한 줄"
        maxLength={80}
      />
      <ChallengeInlineText
        as="h1"
        className="cp-chl-hero-title"
        value={intro.headline || title}
        editable={!!edit}
        onChange={(v) => edit?.setIntro('headline', v.trim() === title.trim() ? '' : v)}
        placeholder="챌린지 제목"
        label="챌린지 제목"
        maxLength={120}
      />
      <div className={`cp-chl-hero-tile${intro.thumbnailUrl ? ' has-image' : ''}`}>
        {intro.thumbnailUrl ? <img src={intro.thumbnailUrl} alt="" /> : <FlagIcon />}
      </div>
      <ChallengeInlineText
        as="p"
        className="cp-chl-hero-desc"
        value={intro.description}
        editable={!!edit}
        onChange={(v) => edit?.setIntro('description', v)}
        placeholder="챌린지 프로젝트 설명 (비우면 숨김)"
        label="챌린지 프로젝트 설명"
        multiline
        maxLength={2000}
      />
    </div>
    <div className="cp-chl-progress-card">
      <div className="cp-chl-progress-label">
        <span>제출 현황</span>
        <b>
          {done}
          <small>/{total}</small>
        </b>
      </div>
      <div className="cp-chl-progress" aria-label={`완료 ${done}/${total}`}>
        <div className="cp-chl-progress-bar" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
      </div>
    </div>
  </>
);

// ───────────────────────── 목록 카드 ─────────────────────────

export type ChallengeCardStatus = 'todo' | 'done' | 'locked';

export const ChallengeListCard: React.FC<{
  item: ChallengeStageItem;
  status?: ChallengeCardStatus;
  onClick?: () => void;
  compact?: boolean;
}> = ({ item, status, onClick, compact }) => {
  const c = item.content;
  const label = status === 'locked' ? '선생님 확인' : status === 'done' ? '제출 완료' : status === 'todo' ? '도전 전' : '';
  const body = (
    <>
      <ChallengeThumb url={item.thumbnailUrl} title={item.title} />
      <div className="cp-chl-card-body">
        {label ? <span className={`cp-chl-status is-${status}`}>{label}</span> : null}
        {c.kicker ? <p className="cp-chl-card-kicker">{c.kicker}</p> : null}
        <h3 className="cp-chl-card-title">{item.title || '제목 없는 챌린지'}</h3>
        {!compact && c.description ? <p className="cp-chl-card-desc">{c.description}</p> : null}
        <p className="cp-chl-card-meta">인증 항목 {c.missions.length}개</p>
      </div>
    </>
  );
  if (!onClick) return <div className={`cp-chl-card${compact ? ' is-compact' : ''}`}>{body}</div>;
  return (
    <button type="button" className={`cp-chl-card${compact ? ' is-compact' : ''}`} onClick={onClick}>
      {body}
    </button>
  );
};
